import type { ReactElement } from 'react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { VirtualItem } from '@tanstack/react-virtual';
import { useVirtualizer } from '@tanstack/react-virtual';
import * as zod from 'zod';
import {
  Badge,
  Box,
  Button,
  DatePicker,
  Input,
  Menu,
  MenuItem,
  MenuSeparator,
  TableHeaders,
  Text,
  TimeField,
  View,
} from '../ui';
import type {
  Currency,
  GetOrderAssignmentsQuery,
  GetOrderAssignmentsQueryVariables,
  Maybe,
  Order,
  PaymentUnit,
  SoftDeleteOrdersMutation,
  SoftDeleteOrdersMutationVariables,
} from '../../graphql';
import {
  GetOrderAssignmentsDocument,
  LoadingUnit,
  SoftDeleteOrdersDocument,
  useVcAndOrdersMutation,
} from '../../graphql';
import type { TableRowProps } from '../ui/Table/Table';
import { TableRow, TableHeader } from '../ui/Table/Table';
import type { TCScheduleEntry } from '../Scenario';
import type {
  MasterSchedule,
  ScenarioSchedule,
} from '@seaber/common/core/scenario';
import type {
  Column,
  Row,
  CellProps,
  HeaderGroup,
  TableState,
  ActionType,
} from 'react-table';
import {
  useExpanded,
  useGlobalFilter,
  useGroupBy,
  useSortBy,
  useTable,
  useFlexLayout,
  useColumnOrder,
} from 'react-table';
import startCase from 'lodash/startCase';
import sortBy from 'lodash/sortBy';
import { parseAmountInfo } from './order';
import useContextMenu from '../hooks/useContextMenu';
import { useLocalSetting } from '../hooks/useLocalConfig';
import type { TableColumnsSelectorProps } from '../../pages/orders';
import isEqual from '@wry/equality';
import type { DragDropData } from '../timeline/ScenarioTimeline';
import { setDragData, setDropEffect } from '../hooks/useDragDrop';
import { midPoint, calculateMassAndVolumeRanges } from '../core/Capacity';
import { isCargoEntry } from '../core/TimeData';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import type { WithRegionNames } from '../timeline/Regions';
import type { ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { StatusFilter } from '../../pages/planning';
import { useNotify } from '../notifications/ToastNotifications';
import useEvent from '../hooks/useEvent';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  hasUserRight,
  useCurrentUser,
  usePlanningMode,
} from '../../state/zustand';
import type {
  IncomingContractType,
  OrderStatus,
  RowInputState,
} from './OrderTableUtils';
import {
  LocationCell,
  NotFoundOrder,
  TextBlock,
  TextCell,
  WEEK_INDICATOR_HEIGHT,
  WeekIndicatorRow,
  getOrderContractType,
  getRowIncomingVoyageCharterId,
  getStateDifference,
  sortDateType,
  sortLocationType,
  timeFormat,
  useExpandByWeek,
  useVCDataToOptions,
  getRowInitialValues,
  laydayToISOWeek,
  useResetSortBy,
  getColumnWidth,
  getOrderStatus,
  isAssortmentValid,
  useScrollToOrder,
} from './OrderTableUtils';
import LocationSelect from '../LocationSelect';
import AutoCompleteSelect from '../AutoCompleteSelect';
import { currencies, paymentUnits } from '../../constants/paymentUnits';
import {
  AcceptButton,
  AddButton,
  ContextMenuIcon,
  DiscardButton,
  EditButton,
} from '../Icons';
import type { BadgeProps } from '../ui/Badge/Badge';
import { useHotkeys } from 'react-hotkeys-hook';
import { isDefined, mapKeyBy, roundTo } from '../../lib/util';
import { atom, useAtom } from 'jotai';
import { persistedAtom } from '../../state/utils/persistedAtom';
import compact from 'lodash/compact';
import { useAllLocations } from '../../lib/allLocations';
import { useGetSynonymMappings } from '../voyageCharter/VoyageCharterForm';
import { getOrderCoaDetails } from '../shipment/getOderCoaDetails';
import type { PnlData } from '../hooks/usePnlData';
import { useContextProp, useContextStore } from '../contexts/ZustandContext';
import type { OrderMenuContextValue } from '../contexts/OrderMenuContext';
import { OrderMenuContext } from '../contexts/OrderMenuContext';

const statusBadges: {
  [key in OrderStatus]: { label: string; variant: BadgeProps['variant'] };
} = {
  assigned: {
    label: 'Assigned',
    variant: 'status/assigned',
  },
  unassigned: {
    label: 'Open',
    variant: 'status/unassigned',
  },
  committed: {
    label: 'Assigned',
    variant: 'status/committed',
  },
  shared: {
    label: 'Published',
    variant: 'status/shared',
  },
  completed: {
    label: 'Completed',
    variant: 'status/shared',
  },
  spot: {
    label: 'Spot',
    variant: 'status/assigned',
  },
  spotCommitted: {
    label: 'Spot',
    variant: 'status/committed',
  },
  spotShared: {
    label: 'Spot',
    variant: 'status/shared',
  },
  coa: {
    label: 'COA',
    variant: 'status/assigned',
  },
  coaCommitted: {
    label: 'COA',
    variant: 'status/committed',
  },
  coaShared: {
    label: 'COA',
    variant: 'status/shared',
  },
};

type OrderTableProps<T extends object> = {
  orders: (WithRegionNames<ScenarioOrder> & {
    assignedVessel: Maybe<string>;
  })[];
  completedOrderIds: Set<number>;
  handleArchiveOrders: () => Promise<boolean>;
  filteredOrderIds: Set<number>;
  onUpdateOrderSharing: (
    orderId: Order['id'] | Order['id'][],
    shared: boolean,
  ) => void;
  onMarkAsLost: (orderIds: number[]) => Promise<boolean>;
  onViewOrder: (orderId: Order['id']) => void;
  onViewVoyageCharter: (voyageCharterId: number) => void;
  onDuplicateOrder: (orderId: Order['id']) => void;
  onDuplicateAndOffsetSelectedOrders: (orderId: Order['id']) => void;
  onDuplicateVoyageCharter: (voyageCharterId: number) => void;
  isSuggestAvailable: boolean;
  onSuggestVoyage: (ids?: number[]) => void;
  onSelectOrder: (orderIds: Order['id'][]) => void;
  onViewOrderShipment: (orderId: Order['id']) => void;
  pnlData: PnlData;
  sharedOrderIds: Set<number>;
  scenario?: ScenarioSchedule;
  masterScenario?: MasterSchedule;
  selectedOrders: Order['id'][];
  showTimes?: boolean;
  onColumnsChange?: (props: TableColumnsSelectorProps<T>) => void;
  selectedStatusFilter: StatusFilter;
  isSearching: boolean;
  getOrderContractName: (order: ScenarioOrder) => string | null;
  onBatchEditSelectedCargoes: (orderId: Order['id']) => void;
  groupCargoesByWeek?: boolean;
};

type OrderTableRowProps = {
  row: Row<OrderTableRowData>;
  onSelect: (
    event: React.MouseEvent<Element, MouseEvent>,
    row: Row<OrderTableRowData>,
    index: number,
  ) => void;
  onContextMenu: (
    event: React.MouseEvent<Element, MouseEvent>,
    row: Row<OrderTableRowData>,
  ) => void;
  onDblClick: (
    event: React.MouseEvent<Element, MouseEvent>,
    row: Row<OrderTableRowData>,
  ) => void;
  selectedOrderIds: Set<Order['id']>;
  index: number;
  selectionMode: boolean;
  isReadOnlyScenario: boolean;
};

const OrderTableRow = React.memo(
  (
    props: OrderTableRowProps &
      TableRowProps & { hiddenColumns: string[]; isLocked: boolean },
  ) => {
    const {
      isLocked,
      row,
      onSelect,
      index,
      onContextMenu,
      onDblClick,
      isMuted,
      isActive,
      selectedOrderIds,
      selectionMode,
      isReadOnlyScenario,
    } = props;

    const elRef = useRef<HTMLDivElement | null>(null);

    const isDraggable =
      row.original.status === 'unassigned' && !isLocked && !isReadOnlyScenario;
    const isSelected = isActive && !isLocked;
    const hasNotices = row.original.hasNotices;

    const getItemMenuButtonProps = useContextProp(
      OrderMenuContext,
      'getItemMenuButtonProps',
    );
    return (
      <TableRow
        data-testid="orders-table-row-draggable"
        aria-selected={isSelected}
        as="div"
        ref={elRef}
        draggable={isDraggable}
        onDragStart={(e) => {
          setDragData(e, {
            type: 'orders',
            selectedOrderIds: [...selectedOrderIds],
            dragOrderId: row.original?.id,
          } as DragDropData);
        }}
        onDragEnd={() => {
          setDropEffect('none', '', true);
        }}
        onDoubleClick={(event) => !isLocked && onDblClick(event, row)}
        onClick={(event) => {
          if (selectionMode || isLocked) return;
          onSelect(event, row, index);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          onContextMenu(e, row);
        }}
        isMuted={isMuted}
        isActive={isSelected}
        height={40}
        style={{
          display: 'flex',
          flex: '1 0 auto',
          alignItems: 'center',
          ...(hasNotices && !isSelected && { backgroundColor: '#ff7b7b3d' }),
        }}
        position="relative"
      >
        {row.cells.map((cell) => {
          const cellProps = cell.getCellProps();
          const isEditColumn = cell.column.id === 'edit';
          const isWeekColumn = cell.column.id === 'week';

          if (isWeekColumn) {
            return null;
          }

          return (
            // eslint-disable-next-line react/jsx-key
            <div
              {...cellProps}
              style={{
                ...cellProps.style,
                maxHeight: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                padding: '0 4px',
                userSelect: 'none',
                lineHeight: 1,
                cursor: 'default',
                borderRight: isEditColumn ? '1px solid #ddd' : 'none',
                backgroundColor: isSelected ? '#ffe552' : 'transparent',
                left: isWeekColumn ? '-40px' : '0px',
              }}
            >
              {cell.isPlaceholder ? null : cell.render('Cell')}
            </div>
          );
        })}
        {getItemMenuButtonProps && (
          <Box position="sticky" right={3} top={3}>
            <ContextMenuIcon
              {...getItemMenuButtonProps({
                onPointerDown: (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onContextMenu(e, row);
                },
              })}
            />
          </Box>
        )}
      </TableRow>
    );
  },
  isEqual,
);

export type OrderTableGlobalFilter = {
  visibleOrderIds: Set<number>;
};

export type OrderTableRowData = WithRegionNames<ScenarioOrder> & {
  status: OrderStatus | null;
  isShared: boolean;
  assignedVessel: Maybe<string>;
  contractType: IncomingContractType | null;
  contractName: string | null;
  startOfWeek: number | null;
  tempId?: number;
  isMultiParcel?: boolean;
  numberOfParcels?: number;
  hasNotices?: boolean;
  freight: {
    freightAmount: number;
    freightUnit: PaymentUnit;
    currency: Currency;
  } | null;
};

export async function softDeleteOrders(
  apolloClient: ApolloClient<object>,
  orderIds: number[],
) {
  const cleanup = await apolloClient.query<
    GetOrderAssignmentsQuery,
    GetOrderAssignmentsQueryVariables
  >({
    query: GetOrderAssignmentsDocument,
    variables: { ids: orderIds },
    fetchPolicy: 'no-cache',
  });

  const orders = cleanup.data.allOrders?.nodes ?? [];
  for (const o of orders) {
    if (
      o &&
      (o.scheduledOrdersByOrderId?.nodes.length > 0 ||
        o.handledByCoaByOrderId ||
        o.handledBySpotByOrderId)
    ) {
      throw new Error(
        `Order ${o.id} is currently scheduled, cannot soft-delete`,
      );
    }
  }

  await apolloClient.query<
    SoftDeleteOrdersMutation,
    SoftDeleteOrdersMutationVariables
  >({
    query: SoftDeleteOrdersDocument,
    variables: { ids: orderIds },
  });
}

function getDefaultColumnOrder(columns: Column<OrderTableRowData>[]) {
  return columns.map((col) => col.id).filter(isDefined);
}

export interface OrderTableRef {
  scrollToOrderId: (orderId: number) => void;
}

type Props = OrderTableProps<OrderTableRowData> & {
  reApplyCargoAllocations?: (orderIds: number[]) => void;
};
const OrderTable = forwardRef<OrderTableRef, Props>(
  (props, ref): ReactElement => {
    const {
      orders,
      completedOrderIds,
      handleArchiveOrders,
      filteredOrderIds,
      onViewOrder,
      onViewVoyageCharter,
      onDuplicateOrder,
      onDuplicateAndOffsetSelectedOrders,
      onDuplicateVoyageCharter,
      isSuggestAvailable,
      onSuggestVoyage,
      onViewOrderShipment,
      onUpdateOrderSharing,
      onMarkAsLost,
      onSelectOrder,
      pnlData,
      sharedOrderIds,
      scenario,
      masterScenario,
      selectedOrders,
      onColumnsChange,
      showTimes,
      selectedStatusFilter,
      isSearching,
      getOrderContractName,
      onBatchEditSelectedCargoes,
      groupCargoesByWeek,
      reApplyCargoAllocations,
    } = props;
    const planningMode = usePlanningMode();

    const voyageCharterMode = planningMode === 'voyage_charter';
    const inquiryMode = useMemo(
      () => selectedStatusFilter === StatusFilter.INQUIRY,
      [selectedStatusFilter],
    );
    const selectionMode = useMemo(
      () => selectedStatusFilter === StatusFilter.TIMELINE_SELECTION,
      [selectedStatusFilter],
    );

    const [shiftAnchorRow, setShiftAnchorRow] = useState<number | null>(null);

    const { organizationId } = useCurrentUser() || {};
    const hasFinancialRights =
      organizationId && hasUserRight(organizationId, 'accessFinancialData');

    const scheduleByOrderId = useMemo(() => {
      const map = new Map<number | string, TCScheduleEntry>();
      const schedule = scenario?.schedule;

      if (schedule) {
        for (let i = 0, l = schedule.length; i < l; i++) {
          const s = schedule[i];
          if ('orderId' in s) {
            map.set(s.orderId, s);
          }
        }
      }

      return map;
    }, [scenario?.schedule]);

    const orderStatus = useCallback(
      (orderId: ScenarioOrder['id']): OrderStatus | null => {
        if (!scenario) return null;

        return getOrderStatus(
          orderId,
          scenario,
          scheduleByOrderId,
          completedOrderIds,
        );
      },
      [completedOrderIds, scenario, scheduleByOrderId],
    );

    const [clientData, setClientData] = useState<OrderTableRowData[]>([]);

    const orderIdsByVoyageCharterId = useMemo(() => {
      const result = new Map<number, Set<number>>();

      orders.forEach((order) => {
        const voyageCharterId =
          order.incomingVoyageCharterRelation?.voyageCharterId;
        if (voyageCharterId) {
          let ids = result.get(voyageCharterId);
          if (!ids) {
            ids = new Set();
            result.set(voyageCharterId, ids);
          }
          ids.add(order.id);
        }
      });

      return result;
    }, [orders]);

    const data = useMemo(() => {
      const incomingCoas = Array.from(
        pnlData.incomingVoyageChartersById.values(),
      ).flatMap(
        (vc) =>
          vc?.voyageCharterToContractsByVoyageCharterId.nodes.at(0)
            ?.coaContractByCoaId,
      );

      const incomingCoasById = mapKeyBy(compact(incomingCoas), (c) => c.id);

      const initialData: OrderTableRowData[] = orders.map((order) => {
        const layday = order.layday;

        const hasNotices = !!order.apiOrdersNoticesByOrderId?.nodes.length;
        const contractType = voyageCharterMode
          ? getOrderContractType(order)
          : null;

        const cargoRanges = calculateMassAndVolumeRanges(
          {
            cargoUnitByCargoUnitId: order.cargoUnitByCargoUnitId,
            cargoAmount: order.cargoAmount,
            marginAmount: order.marginAmount,
            marginUnit: order.marginUnit,
            specificGravity:
              order.assortmentByAssortmentId?.specificGravity ?? null,
            stowageFactorMin:
              order.assortmentByAssortmentId?.stowageFactorMin ?? null,
            stowageFactorMax:
              order.assortmentByAssortmentId?.stowageFactorMax ?? null,
          },
          false,
        );

        let freight: OrderTableRowData['freight'] = null;

        const voyageCharter =
          order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId;
        const voyageCharterFreight =
          order.incomingVoyageCharterRelation?.voyageCharterOrderFreightsByOrderVoyageCharterRelationId.nodes.at(
            0,
          );

        if (
          voyageCharterFreight?.freightAmount &&
          voyageCharterFreight.freightUnit &&
          voyageCharter?.currency
        ) {
          freight = {
            freightAmount: +voyageCharterFreight.freightAmount,
            freightUnit: voyageCharterFreight.freightUnit,
            currency: voyageCharter.currency,
          };
        }

        if (freight === null && contractType === 'COA') {
          const vcRel = order.incomingVoyageCharterRelation;
          const coaId =
            vcRel?.voyageCharterByVoyageCharterId?.voyageCharterToContractsByVoyageCharterId.nodes.at(
              0,
            )?.coaId;
          const coa = coaId ? incomingCoasById.get(coaId) : null;
          const coaRoutes = compact(
            coa?.coaContractPortsByCoaContractId.nodes,
          ).map((route) => ({
            ...route,
            originId: route.originId ?? null,
            destinationId: route.destinationId ?? null,
            originZoneId: route.originZoneId ?? null,
            destinationZoneId: route.destinationZoneId ?? null,
            originZoneLocations: null,
            destinationZoneLocations: null,
          }));

          const { coaAssortment, coaFreight } = getOrderCoaDetails(
            {
              assortmentId: order.assortmentByAssortmentId?.id ?? null,
              assortmentGroupId:
                order.assortmentByAssortmentId?.assortmentGroup ?? null,
              loadPortId: order.loadPortId ?? null,
              dischargePortId: order.dischargePortId,
            },

            {
              mass: midPoint(cargoRanges.mass),
              volume: midPoint(cargoRanges.volume),
            },
            {
              cargoUnitName: coa?.cargoUnitByAmountUnitId?.name ?? null,
              coaAssortments: compact(
                coa?.coaContractAssortmentsByCoaContractId.nodes,
              ).map((assortment) => ({
                ...assortment,
                assortmentId: assortment.assortmentId ?? null,
                assortmentGroupId: assortment.assortmentGroupId ?? null,
              })),
              routes: coaRoutes,
              freights: compact(coa?.coaContractPricesByCoaContractId.nodes),
            },
            vcRel?.coaRouteId ?? null,
          );

          if (coaFreight && coaAssortment?.paymentUnit && coa?.currency) {
            freight = {
              freightAmount: +coaFreight.price,
              freightUnit: coaAssortment.paymentUnit,
              currency: coa.currency,
            };
          }
        }

        return {
          ...order,
          status: orderStatus(order.id),
          contractType,
          contractName: getOrderContractName(order),
          isShared: sharedOrderIds.has(order.id),
          startOfWeek: layday ? laydayToISOWeek.get(layday) : null,
          hasNotices,
          freight,
        };
      });

      return sortBy(
        initialData.concat(clientData),
        (d) => d.incomingVoyageCharterRelation?.voyageCharterId,
      ).map((d) => {
        const voyageCharterId =
          d.incomingVoyageCharterRelation?.voyageCharterId;
        const numberOfParcels = orderIdsByVoyageCharterId.get(
          voyageCharterId ?? NaN,
        )?.size;

        return { ...d, numberOfParcels };
      });
    }, [
      orders,
      clientData,
      orderIdsByVoyageCharterId,
      orderStatus,
      pnlData.incomingVoyageChartersById,
      voyageCharterMode,
      getOrderContractName,
      sharedOrderIds,
    ]);

    /**
     * Row states for editable cells
     */
    const [tempOrderIds, setTempOrderIds] = useState<Set<number>>(new Set());
    const [editedRows, setEditedRows] = useState<Set<number>>(new Set());
    const isEditing = editedRows.size > 0 || tempOrderIds.size > 0;
    const currentEditingRow = useRef<Row<OrderTableRowData> | null>(null);

    const rowStateRef = useRef<Record<number, Partial<RowInputState>>>({});

    useEffect(() => {
      setEditedRows(new Set());
      setTempOrderIds(new Set());
    }, [selectedStatusFilter]);

    const unLockRow = useCallback((rowIndex: number, tempId?: number) => {
      // Remove row from edited rows
      setEditedRows((prev) => {
        const newEditedRows = new Set(prev);
        newEditedRows.delete(rowIndex);
        return newEditedRows;
      });
      // Remove new temporary order if exists
      if (tempId) {
        setTempOrderIds((prev) => {
          const newTempOrderIds = new Set(prev);
          newTempOrderIds.delete(tempId);
          return newTempOrderIds;
        });
        setClientData((prev) => prev.filter((o) => o.tempId !== tempId));
      }
      // Reset row state
      delete rowStateRef.current[rowIndex];
    }, []);

    const onDiscardHandler = useCallback(
      (rowIndex: number, tempId?: number) => {
        unLockRow(rowIndex, tempId);
      },
      [unLockRow],
    );

    const onEditHandler = useCallback((rowIndex: number) => {
      setEditedRows((prev) => new Set(prev.add(rowIndex)));
    }, []);

    const onAddOrder = useCallback(
      (order: OrderTableRowData, isMultiParcel = false) => {
        const tempId = Date.now(); // Random id for temporary orders
        const blankOrder: OrderTableRowData = {
          tempId,
          isMultiParcel,
          id: order.id,
          archived: false,
          assortmentId: null,
          cargoAmount: 0,
          freight: null,
          incomingVoyageCharterRelation: isMultiParcel
            ? order.incomingVoyageCharterRelation
            : null,
          scheduledOrdersByOrderId: { nodes: [] },
          orderPortCallRelationsByOrderId: { nodes: [] },
          orderRequirementAssociationsByOrderId: { nodes: [] },
          orderDetailsByOrderId: { nodes: [] },
          orderAttributeAssociationsByOrderId: { nodes: [] },
          apiOrdersNoticesByOrderId: { nodes: [] },
          loadingWindow: { start: undefined, end: undefined },
          dischargingWindow: { start: undefined, end: undefined },
          cargoRanges: { mass: { min: 0, max: 0 }, volume: { min: 0, max: 0 } },
          cargoAllocations: [],
          dischargePortId: NaN,
          loadPortId: NaN,
          shipmentId: null,
          shipment: null,
          fromRegion: null,
          toRegion: null,
          assignedVessel: null,
          isShared: false,
          status: 'unassigned',
          contractType: 'INQUIRY',
          layday: order.layday ?? null,
          startOfWeek: order.startOfWeek ?? null,
          contractName: null,
        };
        setClientData((prev) => [...prev, blankOrder]);
        setTempOrderIds((prev) => new Set(prev.add(tempId)));
      },
      [],
    );

    // Utility function to get row data
    const getRowData = useCallback(
      (row: Row<OrderTableRowData>) => {
        let isEditing: boolean;
        const order = row.original;
        const rowIndex = row.index;

        if (order.tempId) {
          isEditing = true;
        } else {
          isEditing = editedRows.has(rowIndex);
        }

        const setRowState = (state: Partial<RowInputState>) => {
          rowStateRef.current[rowIndex] = {
            ...rowStateRef.current[rowIndex],
            ...state,
          };
        };
        return { rowIndex, order, isEditing, setRowState };
      },
      [editedRows],
    );

    const notifyEdit = useNotify();
    const [vcAndOrdersMutation] = useVcAndOrdersMutation({
      refetchQueries: ['VoyageCharter'],
    });

    const { data: allLocationsData } = useAllLocations();

    const {
      assortments,
      charterers,
      cargoUnits,
      data: vcData,
    } = useVCDataToOptions();

    const getSynonymMappings = useGetSynonymMappings(
      compact(allLocationsData?.allLocations?.nodes),
      compact(vcData?.allAssortments?.nodes),
    );

    const onAcceptHandler = useCallback(
      async (rowIndex: number, order: OrderTableRowData) => {
        const toastId = `order-${order.id}-edit`;
        try {
          const rowState = rowStateRef.current[rowIndex];
          if (!rowState) {
            notifyEdit('No changes detected', {
              toastId,
              type: 'warning',
            });
            return;
          }

          const {
            assortmentId,
            cancelling,
            cargoAmount,
            cargoUnitId,
            chartererId,
            currency,
            deliveredBefore,
            dischargePortId,
            extraInfo,
            freightAmount,
            freightUnit,
            layday,
            loadPortId,
            orderName,
            rta,
          } = rowState;

          // Date validation
          const laydayDate = layday ?? order.layday;
          const cancellingDate = cancelling ?? order.cancelling;
          const rtaDate = rta ?? order.rta;
          const deliveredBeforeDate = deliveredBefore ?? order.deliveredBefore;

          if (laydayDate && cancellingDate && laydayDate > cancellingDate) {
            notifyEdit(
              `Loading window end needs to be later than start for ${
                order.tempId ? 'new order' : `order #${order.id}`
              }`,
              {
                toastId,
                type: 'error',
              },
            );
            return;
          }
          if (rtaDate && deliveredBeforeDate && rtaDate > deliveredBeforeDate) {
            notifyEdit(
              `Discharge window end needs to be later than start for ${
                order.tempId ? 'new order' : `order #${order.id}`
              }`,
              {
                toastId,
                type: 'error',
              },
            );
            return;
          }
          if (
            laydayDate &&
            deliveredBeforeDate &&
            laydayDate > deliveredBeforeDate
          ) {
            notifyEdit(
              `Discharge window end needs to be later than loading window start for ${
                order.tempId ? 'new order' : `order #${order.id}`
              }`,
              {
                toastId,
                type: 'error',
              },
            );
            return;
          }
          if (laydayDate && rtaDate && laydayDate > rtaDate) {
            notifyEdit(
              `Discharge window start needs to be later than loading window start for ${
                order.tempId ? 'new order' : `order #${order.id}`
              }`,
              {
                toastId,
                type: 'warning',
              },
            );
          }

          const orderId = order.id;
          const orderDetailsId = order.orderDetailsByOrderId.nodes.at(0)?.id;
          const {
            voyageCharterOrderFreightsByOrderVoyageCharterRelationId:
              vcFreights,
            voyageCharterByVoyageCharterId,
            id: ovcRelationId,
          } = order.incomingVoyageCharterRelation || {};

          const vcId = voyageCharterByVoyageCharterId?.id;
          const vcFreightId = vcFreights?.nodes.at(0)?.id;

          const isNewOrder = order.tempId;
          const isMultiParcel = order.isMultiParcel;
          if (isNewOrder) {
            function checkAndNotify<T>(
              field: T | null | undefined,
              fieldName: string,
            ): field is T {
              if (field === null || field === undefined) {
                notifyEdit(`${fieldName} is missing`, {
                  toastId,
                  type: 'error',
                });
                return false;
              }
              return true;
            }

            if (
              !checkAndNotify(loadPortId, 'Load port') ||
              !checkAndNotify(dischargePortId, 'Discharge port') ||
              !checkAndNotify(cargoAmount, 'Cargo amount') ||
              !checkAndNotify(assortmentId, 'Commodity name') ||
              !checkAndNotify(cargoUnitId, 'Cargo unit')
            ) {
              return;
            }
            await vcAndOrdersMutation({
              variables: {
                createOrders: [
                  {
                    assortmentId,
                    cargoAmount,
                    dischargePortId,
                    cargoUnitId,
                    loadPortId,
                    cancelling,
                    rta,
                    deliveredBefore,
                    chartererOrganizationId: chartererId,
                    layday: layday ?? order.layday,
                    organizationId,
                    softDelete: false,
                    loadingRate: 1,
                    loadingUnit:
                      LoadingUnit.LOADING_IN_DAYS_FOR_THE_WHOLE_PARCEL,
                    dischargingRate: 1,
                    dischargingUnit:
                      LoadingUnit.LOADING_IN_DAYS_FOR_THE_WHOLE_PARCEL,
                  },
                ],
                createOrderDetails: [
                  {
                    orderId: { created: 0 },
                    orderName,
                    extraInfo,
                  },
                ],
                createOrderVoyageCharterRelations: [
                  {
                    orderId: { created: 0 },
                    voyageCharterId: {
                      ...(isMultiParcel ? { existing: vcId } : { created: 0 }),
                    },
                    outgoing: false,
                    coaRouteId: null,
                  },
                ],
                ...(!isMultiParcel && {
                  createVoyageCharters: [
                    {
                      currency,
                      isInquiry: true,
                      isPreliminary: false,
                      chartererOrgId: chartererId,
                      shipownerOrgId: organizationId,
                      organisationId: organizationId ?? NaN,
                    },
                  ],
                }),
                createVoyageCharterOrderFreights: [
                  {
                    orderVoyageCharterRelationId: { created: 0 },
                    freightAmount,
                    freightUnit,
                  },
                ],
              },
            });
          } else {
            // Existing order
            const initialValues = getRowInitialValues(order);
            const diff = getStateDifference(initialValues, rowState);
            const hasChanges = Object.keys(diff).length > 0;
            if (!hasChanges) {
              notifyEdit(`No changes detected for order #${order.id}`, {
                toastId,
                type: 'warning',
              });
              unLockRow(rowIndex);
              return;
            }

            const { mapLocations, mapAssortments } = await getSynonymMappings([
              [order, rowState],
            ]);

            await vcAndOrdersMutation({
              variables: {
                mapLocations,
                mapAssortments,
                updateOrders: [
                  {
                    id: orderId,
                    orderPatch: {
                      ...(assortmentId && { assortmentId }),
                      ...(cargoAmount && { cargoAmount }),
                      ...(cargoUnitId && { cargoUnitId }),
                      ...(dischargePortId && { dischargePortId }),
                      ...(loadPortId && { loadPortId }),
                      chartererOrganizationId: chartererId,
                      layday,
                      cancelling,
                      rta,
                      deliveredBefore,
                    },
                  },
                ],
                ...(isDefined(orderDetailsId)
                  ? {
                      updateOrderDetails: [
                        {
                          id: orderDetailsId,
                          orderDetailPatch: {
                            orderName,
                            extraInfo,
                          },
                        },
                      ],
                    }
                  : {
                      createOrderDetails: [
                        {
                          orderId: { existing: orderId },
                          orderName,
                          extraInfo,
                        },
                      ],
                    }),
                ...(isDefined(vcId) && {
                  updateVoyageCharters: [
                    {
                      id: vcId,
                      voyageCharterPatch: {
                        currency,
                        chartererOrgId: chartererId,
                      },
                    },
                  ],
                }),
                ...(isDefined(vcFreightId) && {
                  updateVoyageCharterOrderFreights: [
                    {
                      id: vcFreightId,
                      voyageCharterOrderFreightPatch: {
                        freightAmount,
                        freightUnit,
                      },
                    },
                  ],
                }),
                ...(!isDefined(vcFreightId) &&
                  isDefined(ovcRelationId) &&
                  isDefined(freightAmount) &&
                  freightUnit && {
                    createVoyageCharterOrderFreights: [
                      {
                        orderVoyageCharterRelationId: {
                          existing: ovcRelationId,
                        },
                        freightAmount,
                        freightUnit,
                      },
                    ],
                  }),
              },
            });
          }

          if (reApplyCargoAllocations) {
            reApplyCargoAllocations([orderId]);
          }

          const notifyMessage = isMultiParcel
            ? `New parcel for order #${order.id} created`
            : isNewOrder
              ? 'Inquiry order successfully created'
              : `Order #${order.id} updated`;
          notifyEdit(notifyMessage, { toastId, type: 'success' });
          unLockRow(rowIndex, order.tempId);
          currentEditingRow.current = null;
        } catch (e) {
          console.error(e);
          notifyEdit(e.message, { toastId, type: 'error' });
        }
      },
      [
        notifyEdit,
        unLockRow,
        vcAndOrdersMutation,
        organizationId,
        getSynonymMappings,
        reApplyCargoAllocations,
      ],
    );

    useHotkeys(
      'enter',
      () => {
        if (currentEditingRow.current) {
          const { rowIndex, order } = getRowData(currentEditingRow.current);
          void onAcceptHandler(rowIndex, order);
        }
      },
      { enableOnFormTags: ['INPUT', 'SELECT'], enabled: isEditing },
    );

    const orderNameWidth = getColumnWidth(data, 'orderName');
    const externalOrderIdWidth = getColumnWidth(data, 'externalOrderId');
    const remarksWidth = getColumnWidth(data, 'remarks');

    const columns: Column<OrderTableRowData>[] = useMemo(
      () => [
        {
          id: 'week',
          Header: 'Week',
          accessor: (row) => row.startOfWeek,
        },
        {
          id: 'edit',
          Header: 'Edit',
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, rowIndex, isEditing } = getRowData(row);
            return (
              <>
                {isEditing ? (
                  <Box display="flex">
                    <AcceptButton
                      onPress={() => onAcceptHandler(rowIndex, order)}
                    />
                    <DiscardButton
                      onPress={() => onDiscardHandler(rowIndex, order?.tempId)}
                    />
                  </Box>
                ) : (
                  <Box display="flex">
                    <EditButton onPress={() => onEditHandler(rowIndex)} />
                    {inquiryMode ? (
                      <AddButton onPress={() => onAddOrder(order)} />
                    ) : null}
                  </Box>
                )}
              </>
            );
          },
          width: 50,
          sticky: 'left',
        },
        {
          id: 'id',
          Header: 'Id',
          accessor: (order) =>
            order?.tempId
              ? `#${order.isMultiParcel ? 'New-Parcel' : 'New'}`
              : `#${order.id}`,
          Cell: TextCell,
          width: 70,
        },
        {
          id: 'numberOfParcels',
          Header: 'Parcels',
          accessor: (order) => {
            return order.isMultiParcel
              ? (order.numberOfParcels ?? 1) + 1
              : (order.numberOfParcels ?? 1);
          },
          Cell: TextCell,
          width: 60,
        },
        {
          id: 'orderName',
          Header: 'Order name',
          accessor: (order) =>
            order.orderDetailsByOrderId.nodes.at(0)?.orderName ?? '',
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const orderName =
              order.orderDetailsByOrderId.nodes.at(0)?.orderName;
            const [localValue, setLocalValue] = useState<string>();

            const displayValue = localValue ?? orderName ?? '';

            return (
              <>
                {isEditing ? (
                  <Input
                    type="text"
                    placeholder="Order name"
                    value={displayValue}
                    onChange={(e) => {
                      currentEditingRow.current = row;
                      const newValue = e.target.value;
                      setLocalValue(newValue);
                      setRowState({ orderName: newValue || null });
                    }}
                  />
                ) : (
                  <TextBlock value={displayValue} />
                )}
              </>
            );
          },
          width: orderNameWidth,
        },
        {
          id: 'locationByLoadPortId',
          Header: 'From',
          accessor: (order) => order.locationByLoadPortId,
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<ScenarioLocation>();

            const displayValue = localValue ?? order.locationByLoadPortId;

            return (
              <>
                {isEditing ? (
                  <LocationSelect
                    isClearable={false}
                    value={{
                      value: displayValue?.id,
                      label: displayValue?.name,
                    }}
                    onChange={(loc) => {
                      currentEditingRow.current = row;
                      const newValue = loc?.location as ScenarioLocation;
                      setLocalValue(newValue);
                      setRowState({ loadPortId: newValue.id });
                    }}
                  />
                ) : (
                  <LocationCell value={displayValue} />
                )}
              </>
            );
          },
          sortType: sortLocationType,
        },
        {
          id: 'fromRegion',
          Header: 'From Region',
          accessor: (order) => order.fromRegion ?? null,
          Cell: TextCell,
          width: 140,
        },
        {
          id: 'locationByDischargePortId',
          Header: 'To',
          accessor: (order) => order.locationByDischargePortId,
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<ScenarioLocation>();

            const displayValue = localValue ?? order.locationByDischargePortId;

            return (
              <>
                {isEditing ? (
                  <LocationSelect
                    isClearable={false}
                    value={{
                      value: displayValue?.id,
                      label: displayValue?.name,
                    }}
                    onChange={(loc) => {
                      currentEditingRow.current = row;
                      const newValue = loc?.location as ScenarioLocation;
                      setLocalValue(newValue);
                      setRowState({ dischargePortId: newValue.id });
                    }}
                  />
                ) : (
                  <LocationCell value={displayValue} />
                )}
              </>
            );
          },
          sortType: sortLocationType,
        },
        {
          id: 'toRegion',
          Header: 'To Region',
          accessor: (order) => order.toRegion ?? null,
          Cell: TextCell,
          width: 140,
        },
        {
          id: 'amount',
          Header: 'Amount',
          accessor: (order) => order.cargoAmount ?? null,
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<{
              cargoAmount: number | string | null;
              cargoUnitId: number | null;
            }>();

            const displayValue = {
              cargoAmount: localValue?.cargoAmount ?? order.cargoAmount,
              cargoUnitId:
                localValue?.cargoUnitId ??
                order.cargoUnitByCargoUnitId?.id ??
                null,
            };

            return (
              <>
                {isEditing ? (
                  <Box display="flex">
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      width={100}
                      value={displayValue.cargoAmount}
                      onChange={(e) => {
                        currentEditingRow.current = row;
                        const newValue = Number(e.target.value);
                        setLocalValue({
                          ...displayValue,
                          cargoAmount: newValue,
                        });
                        setRowState({ cargoAmount: newValue });
                      }}
                    />
                    <Box width={80}>
                      <AutoCompleteSelect
                        isClearable={false}
                        options={cargoUnits}
                        value={displayValue.cargoUnitId}
                        onChange={(newValue) => {
                          currentEditingRow.current = row;
                          if (!newValue) return;
                          setLocalValue({
                            ...displayValue,
                            cargoUnitId: newValue,
                          });
                          setRowState({ cargoUnitId: newValue });
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Text
                    isUnselectable
                    style={{ display: 'block', width: 'fit-content' }}
                  >
                    {parseAmountInfo({
                      ...order,
                      cargoAmount:
                        order.cargoUnitByCargoUnitId?.name === 'pcs' ||
                        order.cargoUnitByCargoUnitId?.name === 'set'
                          ? displayValue.cargoAmount
                          : roundTo(displayValue.cargoAmount, 0),
                    })}
                  </Text>
                )}
              </>
            );
          },
          sortType: (rowA, rowB): number => {
            const a = +rowA.original?.cargoAmount;
            const b = +rowB.original?.cargoAmount;
            return a === b ? 0 : a > b ? 1 : -1;
          },
        },
        {
          id: 'commodityGroup',
          Header: 'Commodity group',
          Cell: TextCell,
          accessor: (order) =>
            order.assortmentByAssortmentId?.assortmentGroupByAssortmentGroup
              ?.name ?? '',
        },
        {
          id: 'assortment',
          Header: 'Commodity',
          Cell: ({ row }) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<{
              name: string | null;
              id: number | null;
            }>();

            const displayValue = localValue ?? order.assortmentByAssortmentId;
            const isInvalid = !isAssortmentValid(
              order.assortmentByAssortmentId,
            );

            return (
              <>
                {isEditing ? (
                  <AutoCompleteSelect
                    placeholder="Commodity"
                    isClearable={false}
                    options={assortments}
                    value={displayValue?.id}
                    onChange={(e) => {
                      currentEditingRow.current = row;
                      const newValue = assortments.find(
                        (a) => a.value === Number(e),
                      );
                      if (!newValue) return;
                      setLocalValue({
                        name: newValue.label,
                        id: newValue.value,
                      });
                      setRowState({ assortmentId: newValue.value });
                    }}
                  />
                ) : (
                  <TextBlock
                    value={displayValue?.name ?? ''}
                    isInvalid={isInvalid}
                  />
                )}
              </>
            );
          },
        },
        {
          id: 'freight',
          Header: 'Freight',
          Cell: ({ row }) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const isTempOrder = order.tempId;
            const isMultiParcel = order.isMultiParcel;
            const { voyageCharterByVoyageCharterId } =
              order.incomingVoyageCharterRelation || {};
            const { freightAmount, freightUnit } = isTempOrder
              ? { freightAmount: undefined, freightUnit: undefined }
              : order.freight || {};
            const currency =
              isTempOrder && !isMultiParcel
                ? undefined
                : voyageCharterByVoyageCharterId?.currency;

            const [localValue, setLocalValue] = useState<{
              freightAmount: number | string | null | undefined;
              freightUnit: PaymentUnit | null | undefined;
              currency: Currency | null | undefined;
            }>();

            const displayValue = {
              freightAmount: localValue?.freightAmount ?? freightAmount,
              freightUnit: localValue?.freightUnit ?? freightUnit,
              currency: localValue?.currency ?? currency,
            };

            const paymentUnitOptions = paymentUnits.map(({ value }) => ({
              label: value,
              value,
            }));
            const currencyOptions = currencies.map(({ value }) => ({
              label: value,
              value,
            }));

            return (
              <>
                {isEditing ? (
                  <Box display="flex">
                    <Input
                      placeholder="Amount"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      width={100}
                      value={displayValue.freightAmount ?? ''}
                      onChange={(e) => {
                        currentEditingRow.current = row;
                        const newValue = Number(e.target.value);
                        setLocalValue({
                          ...displayValue,
                          freightAmount: newValue,
                        });
                        setRowState({ freightAmount: newValue || null });
                      }}
                    />
                    <Box width={60}>
                      <AutoCompleteSelect
                        isDisabled={isMultiParcel}
                        placeholder="$"
                        isClearable={false}
                        options={currencyOptions}
                        value={displayValue.currency}
                        onChange={(newValue: Currency) => {
                          currentEditingRow.current = row;
                          setLocalValue({
                            ...displayValue,
                            currency: newValue,
                          });
                          setRowState({ currency: newValue });
                        }}
                      />
                    </Box>
                    <Box width={90}>
                      <AutoCompleteSelect
                        placeholder="Unit"
                        isClearable={false}
                        options={paymentUnitOptions}
                        value={displayValue.freightUnit}
                        onChange={(newValue: PaymentUnit) => {
                          currentEditingRow.current = row;
                          setLocalValue({
                            ...displayValue,
                            freightUnit: newValue,
                          });
                          setRowState({ freightUnit: newValue });
                        }}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Text
                    isUnselectable
                    style={{ display: 'block', width: 'fit-content' }}
                  >
                    {displayValue.freightAmount ?? ''}
                    {displayValue.currency ? ` ${displayValue.currency}` : ''}
                    {displayValue.freightUnit
                      ? ` ${displayValue.freightUnit}`
                      : ''}
                  </Text>
                )}
              </>
            );
          },
          width: 250,
        },
        {
          id: 'layday',
          Header: 'Earliest loading',
          accessor: (order) => order.layday ?? null,
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<string>();

            const displayValue = localValue ?? order.layday;

            return (
              <>
                {isEditing ? (
                  <DatePicker
                    portalId={`order-table-${order.id}-layday`}
                    showTime
                    showTimeInput
                    onChange={(d) => {
                      currentEditingRow.current = row;
                      const newValue = d?.toISOString();
                      setLocalValue(newValue ?? '');
                      setRowState({ layday: newValue ?? null });
                    }}
                    selected={displayValue ? new Date(displayValue) : null}
                    timeZone="UTC"
                  />
                ) : (
                  <TimeField
                    value={displayValue}
                    formatstring={timeFormat(showTimes)}
                    timeZone={order.locationByLoadPortId?.timezone ?? undefined}
                    isUnselectable
                  />
                )}
              </>
            );
          },
          sortType: sortDateType,
        },
        {
          id: 'cancelling',
          Header: 'Latest loading',
          accessor: (order) => order.cancelling ?? null,
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<string>();

            const displayValue = localValue ?? order.cancelling;

            return (
              <>
                {isEditing ? (
                  <DatePicker
                    portalId={`order-table-${order.id}-cancelling`}
                    showTime
                    showTimeInput
                    onChange={(d) => {
                      currentEditingRow.current = row;
                      const newValue = d?.toISOString();
                      setLocalValue(newValue ?? '');
                      setRowState({ cancelling: newValue ?? null });
                    }}
                    selected={displayValue ? new Date(displayValue) : null}
                    timeZone="UTC"
                  />
                ) : (
                  <TimeField
                    value={displayValue}
                    formatstring={timeFormat(showTimes)}
                    timeZone={order.locationByLoadPortId?.timezone ?? undefined}
                    isUnselectable
                  />
                )}
              </>
            );
          },
          sortType: sortDateType,
        },
        {
          id: 'rta',
          Header: 'Earliest discharging',
          accessor: (order) => order.rta ?? null,
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<string>();

            const displayValue = localValue ?? order.rta;

            return (
              <>
                {isEditing ? (
                  <DatePicker
                    portalId={`order-table-${order.id}-rta`}
                    showTime
                    showTimeInput
                    onChange={(d) => {
                      currentEditingRow.current = row;
                      const newValue = d?.toISOString();
                      setLocalValue(newValue ?? '');
                      setRowState({ rta: newValue ?? null });
                    }}
                    selected={displayValue ? new Date(displayValue) : null}
                    timeZone="UTC"
                  />
                ) : (
                  <TimeField
                    value={displayValue}
                    formatstring={timeFormat(showTimes)}
                    timeZone={
                      order.locationByDischargePortId?.timezone ?? undefined
                    }
                    isUnselectable
                  />
                )}
              </>
            );
          },
          sortType: sortDateType,
          width: 175,
        },
        {
          id: 'deliveredBefore',
          Header: 'Latest discharging',
          accessor: (order) => order.deliveredBefore ?? null,
          Cell: ({ row }: CellProps<OrderTableRowData>) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<string>();

            const displayValue = localValue ?? order.deliveredBefore;

            return (
              <>
                {isEditing ? (
                  <DatePicker
                    portalId={`order-table-${order.id}-deliveredBefore`}
                    showTime
                    showTimeInput
                    onChange={(d) => {
                      currentEditingRow.current = row;
                      const newValue = d?.toISOString();
                      setLocalValue(newValue ?? '');
                      setRowState({ deliveredBefore: newValue ?? null });
                    }}
                    selected={displayValue ? new Date(displayValue) : null}
                    timeZone="UTC"
                  />
                ) : (
                  <TimeField
                    value={displayValue}
                    formatstring={timeFormat(showTimes)}
                    timeZone={
                      order.locationByDischargePortId?.timezone ?? undefined
                    }
                    isUnselectable
                  />
                )}
              </>
            );
          },
          sortType: sortDateType,
          width: 165,
        },
        {
          id: 'remarks',
          Header: 'Remarks',
          Cell: ({ row }) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<string>();

            const displayValue =
              localValue ??
              order.orderDetailsByOrderId.nodes.at(0)?.extraInfo ??
              '';

            return (
              <>
                {isEditing ? (
                  <Input
                    type="text"
                    placeholder="Order remarks"
                    value={displayValue}
                    onChange={(e) => {
                      currentEditingRow.current = row;
                      const newValue = e.target.value;
                      setLocalValue(newValue);
                      setRowState({ extraInfo: newValue || null });
                    }}
                  />
                ) : (
                  <TextBlock value={displayValue} />
                )}
              </>
            );
          },
          width: remarksWidth,
        },
        {
          id: 'externalRemarks',
          Header: 'External Remarks',
          Cell: ({ row }) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<string>();

            const displayValue =
              localValue ??
              order.orderDetailsByOrderId.nodes.at(0)?.externalRemarks ??
              '';

            return (
              <>
                {isEditing ? (
                  <Input
                    type="text"
                    placeholder="Order remarks"
                    value={displayValue}
                    onChange={(e) => {
                      currentEditingRow.current = row;
                      const newValue = e.target.value;
                      setLocalValue(newValue);
                      setRowState({ extraInfo: newValue || null });
                    }}
                  />
                ) : (
                  <TextBlock value={displayValue} />
                )}
              </>
            );
          },
          width: remarksWidth,
        },
        {
          id: 'chartererOrganizationId',
          Header: 'Charterer',
          Cell: ({ row }) => {
            const { order, isEditing, setRowState } = getRowData(row);
            const [localValue, setLocalValue] = useState<{
              name: string | null;
              id: number | null;
            }>();

            const orderChartererId = order.chartererOrganizationId;
            const orderCharterer = charterers.find(
              (i) => i.value === orderChartererId,
            );

            const displayValue = localValue ??
              order.organizationByChartererOrganizationId ?? {
                id: orderCharterer?.value,
                name: orderCharterer?.label,
              };

            return (
              <>
                {isEditing ? (
                  <AutoCompleteSelect
                    isClearable={false}
                    options={[{ label: 'None', value: null }, ...charterers]}
                    value={displayValue?.id}
                    onChange={(value) => {
                      currentEditingRow.current = row;
                      if (value === null) {
                        setLocalValue({ name: null, id: null });
                        setRowState({ chartererId: null });
                      } else {
                        const newValue = charterers.find(
                          (c) => c.value === Number(value),
                        );
                        if (!newValue) return;
                        setLocalValue({
                          name: newValue.label,
                          id: newValue.value,
                        });
                        setRowState({ chartererId: newValue.value });
                      }
                    }}
                  />
                ) : (
                  <TextBlock value={displayValue?.name ?? ''} />
                )}
              </>
            );
          },
        },
        {
          id: 'externalOrderId',
          Header: 'External Id',
          Cell: TextCell,
          accessor: (order) => order.externalOrderId ?? '',
          width: externalOrderIdWidth,
        },
        {
          id: 'voyageExternalId',
          Header: 'Voyage external Id',
          Cell: TextCell,
          accessor: (order) =>
            order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId
              ?.externalId ?? '',
        },
        {
          id: 'fixtureNote',
          Header: 'Fixture note',
          Cell: TextCell,
          accessor: (order) =>
            order.orderDetailsByOrderId.nodes.at(0)?.fixtureNote ?? '',
          width: externalOrderIdWidth,
        },
        {
          id: 'nominatedVessel',
          Header: 'Nominated vessel',
          Cell: TextCell,
          accessor: (order) => order.nominatedVessel ?? '',
          width: 160,
        },
        {
          id: 'assignedVessel',
          Header: 'Assigned Vessel',
          Cell: TextCell,
          accessor: (order) => order.assignedVessel,
        },
        {
          id: 'createdAt',
          Header: 'Created At',
          accessor: 'createdAt',
          Cell: ({ value }) => {
            return (
              <TimeField
                value={value}
                formatstring={timeFormat(showTimes)}
                isUnselectable
              />
            );
          },
        },
        {
          id: 'cargoCreator',
          Header: 'Cargo Creator',
          Cell: TextCell,
          accessor: (order) => order.userByUserId?.name ?? '',
        },
        ...(voyageCharterMode
          ? [
              {
                id: 'contractType',
                Header: 'Contract type',
                accessor: (order: OrderTableRowData) => order.contractType,
                Cell: ({
                  value,
                }: {
                  value: OrderTableRowData['contractType'];
                }) =>
                  value ? (
                    <Badge
                      variant={
                        value === 'INQUIRY' || value === 'PRELIMINARY'
                          ? 'contractStatus/inquiry'
                          : value === 'ON_SUBS'
                            ? 'contractStatus/onSubs'
                            : value === 'SUBS_EXPIRED'
                              ? 'contractStatus/subsExpired'
                              : undefined
                      }
                    >
                      {startCase(value)}
                    </Badge>
                  ) : null,
                width: 120,
              },
            ]
          : []),
        {
          id: 'contractName',
          Header: 'Contract name',
          accessor: (order: OrderTableRowData) => order.contractName,
        },
        {
          id: 'status',
          Header: 'Status',
          accessor: (order) => order.status ?? null,
          Cell: (props: CellProps<OrderTableRowData, OrderStatus | null>) => {
            const statusBadge = props.value ? statusBadges[props.value] : null;
            return statusBadge ? (
              <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
            ) : null;
          },
          width: 100,
        },
        {
          id: 'actions',
          Header: 'Actions',
          align: 'right',
          Cell: (props: CellProps<OrderTableRowData, null>) => {
            const order = props.row.original;

            const status = order.status;
            const isAssigned = status !== 'unassigned';
            const isCommitted =
              status === 'committed' ||
              status === 'coaCommitted' ||
              status === 'spotCommitted';

            const isShared = order.isShared;

            const incomingVoyageCharterId =
              order.incomingVoyageCharterRelation?.voyageCharterId ?? null;

            return (
              <View
                flexDirection="row"
                alignSelf="flex-start"
                justifyContent="flex-end"
              >
                {(isCommitted || isShared) && (
                  <Button
                    variant={isShared ? 'positive' : 'secondary'}
                    onPress={() => onUpdateOrderSharing(order.id, !isShared)}
                    mr={3}
                    width={82}
                  >
                    {isShared ? 'Published' : 'Publish'}
                  </Button>
                )}
                {isAssigned && (
                  <Button
                    variant="secondary"
                    onPress={() => onViewOrderShipment(order.id)}
                    mr={3}
                  >
                    Timeline
                  </Button>
                )}
                {voyageCharterMode && incomingVoyageCharterId !== null ? (
                  <Button
                    variant="secondary"
                    onPress={() => onViewVoyageCharter(incomingVoyageCharterId)}
                  >
                    View
                  </Button>
                ) : (
                  <Button
                    variant="secondary"
                    onPress={() => onViewOrder(order.id)}
                  >
                    View
                  </Button>
                )}
              </View>
            );
          },
          width: 250,
        },
      ],
      [
        onUpdateOrderSharing,
        onViewOrder,
        onViewOrderShipment,
        onViewVoyageCharter,
        voyageCharterMode,
        inquiryMode,
        showTimes,
        // column widths
        orderNameWidth,
        externalOrderIdWidth,
        remarksWidth,
        // row state deps
        getRowData,
        onAddOrder,
        onAcceptHandler,
        onDiscardHandler,
        onEditHandler,
        // select option deps
        cargoUnits,
        assortments,
        charterers,
      ],
    );

    const [hiddenColumns, saveHiddenColumns] = useLocalSetting(
      'planningOrdersTableHiddenColumns',
      ['week'],
    );

    const [orderTableSort, setOrderTableSort] = useLocalSetting(
      'planningOrdersTableSortBy',
      [],
    );

    const [columnOrder, saveColumnOrder] = useAtom(orderTableColumnOrderAtom);

    const globalFilter = useCallback(
      (
        rows: Array<Row<OrderTableRowData>>,
        _columnIds: Array<string>,
        filterValue: OrderTableGlobalFilter,
      ): Array<Row<OrderTableRowData>> => {
        return rows.filter((row) =>
          filterValue.visibleOrderIds.has(row.original?.id),
        );
      },
      [],
    );

    const stateReducer = useCallback(
      (newState: TableState<OrderTableRowData>, action: ActionType) => {
        if (action.type === 'toggleSortBy') {
          setOrderTableSort(newState.sortBy);
          return newState;
        }
        if (action.type === 'toggleHideColumn') {
          saveHiddenColumns(newState.hiddenColumns);
          return newState;
        }
        if (action.type === 'toggleHideAllColumns') {
          saveHiddenColumns(newState.hiddenColumns);
          return newState;
        }

        return newState;
      },
      [setOrderTableSort, saveHiddenColumns],
    );

    const {
      allColumns,
      getTableBodyProps,
      getTableProps,
      getToggleHideAllColumnsProps: toggleHideColumns, // we need to rename this to create a memoized version
      headerGroups,
      prepareRow,
      rows,
      setGlobalFilter,
      totalColumnsWidth,
      setColumnOrder,
      setGroupBy,
    } = useTable<OrderTableRowData>(
      {
        columns,
        data,
        stateReducer,
        initialState: {
          columnOrder:
            columnOrder.length > 0
              ? columnOrder
              : getDefaultColumnOrder(columns),
          hiddenColumns,
          sortBy: orderTableSort,
          groupBy: groupCargoesByWeek ? ['week'] : [],
        },
        globalFilter,
        autoResetGlobalFilter: false,
        autoResetExpanded: false,
      },
      useGlobalFilter,
      useColumnOrder,
      useGroupBy,
      useSortBy,
      useExpanded,
      useFlexLayout,

      (hooks) => {
        hooks.useInstance.push(useExpandByWeek);
        hooks.useInstance.push(useResetSortBy);
      },
    );

    useEffect(() => {
      (setGroupBy as (groupBy: string[]) => void)(
        groupCargoesByWeek ? ['week'] : [],
      );
    }, [groupCargoesByWeek, setGroupBy]);

    useEffect(() => {
      /**
       * Update the global filter when the filtered order IDs change, which is the combination of
       * the status-filtered order IDs and the newly added temporary orders in the inquiry mode.
       */
      setGlobalFilter({
        visibleOrderIds: new Set([...filteredOrderIds, ...tempOrderIds]),
      });
    }, [setGlobalFilter, filteredOrderIds, tempOrderIds]);

    // memoized to avoid re-rendering the table on every render
    const getToggleHideAllColumnsProps = useMemo(
      () => toggleHideColumns,
      [toggleHideColumns],
    );

    useEffect(() => {
      onColumnsChange?.({
        allColumns: allColumns,
        getToggleHideAllColumnsProps,
      });
    }, [
      allColumns,
      onColumnsChange,
      hiddenColumns,
      getToggleHideAllColumnsProps,
    ]);

    const rowsToRender = useMemo(
      () =>
        groupCargoesByWeek ? sortBy(rows, (row) => row.values.week) : rows,
      [groupCargoesByWeek, rows],
    );

    useHotkeys(
      'esc',
      () => {
        // Discard last edited row, one by one from the last row
        const lastRowEditRow = Array.from(editedRows).at(-1);
        const lastRow = rows.find((i) => i.index === lastRowEditRow);
        if (lastRow) {
          const rowIndex = lastRow.index;
          unLockRow(rowIndex);
        }

        // Clear all temp orders
        if (tempOrderIds.size > 0) {
          setTempOrderIds(new Set());
          setClientData([]);
        }
      },
      {
        enableOnFormTags: ['INPUT', 'SELECT'],
        enabled: isEditing,
      },
    );

    useEffect(() => {
      setShiftAnchorRow(null);
    }, [rowsToRender]);

    const selectOrdersFromRows = useCallback(
      (
        event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
        row: Row<OrderTableRowData>,
        index: number,
      ): void => {
        const ordersToSelect: number[] = [];
        if (event.shiftKey && shiftAnchorRow !== null) {
          if (shiftAnchorRow < index) {
            for (let i = shiftAnchorRow; i <= index; i++) {
              const id = rowsToRender[i].original?.id;
              if (!id) continue; // Week rows don't have access to the row object via original
              ordersToSelect.push(rowsToRender[i].original.id);
            }
          } else {
            for (let i = index; i <= shiftAnchorRow; i++) {
              const id = rowsToRender[i].original?.id;
              if (!id) continue; // Week rows don't have access to the row object via original
              ordersToSelect.push(id);
            }
          }
        } else if (event.metaKey || event.ctrlKey) {
          setShiftAnchorRow(index);
          let selected: number[];
          if (selectedOrders.includes(row.original?.id)) {
            selected = selectedOrders.filter((id) => id !== row.original?.id);
          } else {
            selected = selectedOrders;
            selected.push(row.original?.id);
          }
          ordersToSelect.push(...selected);
        } else {
          setShiftAnchorRow(index);
          if (!selectedOrders.includes(row.original?.id)) {
            const voyageCharterId =
              row.original.incomingVoyageCharterRelation?.voyageCharterId;
            ordersToSelect.push(row.original?.id);
            if (voyageCharterId) {
              // If we're in the orphan parcels tab, only select orphan parcels
              if (selectedStatusFilter === StatusFilter.ORPHAN_PARCELS) {
                const orderIds = Array.from(
                  orderIdsByVoyageCharterId.get(voyageCharterId) ?? [],
                ).filter((id) => filteredOrderIds.has(id));

                orderIds.forEach((id) => {
                  if (!selectedOrders.includes(id) && id !== row.original?.id) {
                    ordersToSelect.push(id);
                  }
                });
              } else {
                // Normal behavior for other tabs: add all orders from the same voyage charter
                const orderIds = Array.from(
                  orderIdsByVoyageCharterId.get(voyageCharterId) ?? [],
                );
                orderIds.forEach((id) => {
                  if (!selectedOrders.includes(id)) {
                    ordersToSelect.push(id);
                  }
                });
              }
            }
          }
        }
        onSelectOrder(ordersToSelect);
      },
      [
        shiftAnchorRow,
        onSelectOrder,
        rowsToRender,
        selectedOrders,
        selectedStatusFilter,
        orderIdsByVoyageCharterId,
        filteredOrderIds,
      ],
    );

    const currentContextRow = useRef<Row<OrderTableRowData> | null>(null);

    const {
      onContextMenu: onRowContextMenu,
      styles: rowContextMenuStyles,
      isVisible: isRowContextMenuVisible,
      setMenuRef: setRowContextMenuRef,
      hideMenu: hideRowContextMenu,
      getButtonProps: getItemMenuButtonProps,
    } = useContextMenu();

    const onContextMenu = useEvent(
      (
        event: React.MouseEvent<Element, MouseEvent>,
        row: Row<OrderTableRowData>,
      ) => {
        currentContextRow.current = row;
        onRowContextMenu(event);
      },
    );

    const {
      onContextMenu: onHeaderContextMenu,
      styles: headerContextMenuStyles,
      isVisible: isHeaderContextMenuVisible,
      setMenuRef: setHeaderContextMenuRef,
    } = useContextMenu();

    const notify = useNotify({ toastId: 'handlePublish' });
    const apolloClient = useApolloClient();

    const handlePublish = useEvent(async (orderIds: number[]) => {
      try {
        notify(`Publishing order${orderIds.length > 1 ? 's' : ''} ...`, {
          type: 'info',
        });

        hideRowContextMenu();

        const ids = new Set(orderIds);
        const setShared = !rows?.find((row) => {
          const original = row.original;
          return original && ids.has(original.id) && original.isShared != null;
        })?.original.isShared;

        onUpdateOrderSharing(Array.from(ids), setShared);

        notify(`Order${orderIds.length > 1 ? 's' : ''} published.`, {
          type: 'success',
        });
      } catch (err) {
        notify(
          `Unable to publish order${orderIds.length > 1 ? 's' : ''}: ${
            err.message
          }`,
        );
      }
    });

    const handleArchive = useEvent(async (orderIds: number[]) => {
      try {
        notify(`Archiving order${orderIds.length > 1 ? 's' : ''} ...`, {
          toastId: 'handleArchive',
          type: 'info',
        });

        hideRowContextMenu();

        handleArchiveOrders().then((wasSuccess) => {
          if (!wasSuccess) return;
          notify(`Order${orderIds.length > 1 ? 's' : ''} archived.`, {
            toastId: 'handleArchive',
            type: 'success',
          });
        });
      } catch (err) {
        notify(
          `Unable to archive order${orderIds.length > 1 ? 's' : ''}: ${
            err.message
          }`,
          {
            toastId: 'handleArchive',
            type: 'info',
          },
        );
      }
    });

    const handleMarkAsLost = useEvent(async (orderIds: number[]) => {
      try {
        hideRowContextMenu();
        await onMarkAsLost(orderIds);
      } catch (err) {
        notify(`Unable to mark some orders as lost: ${err.message}`, {
          toastId: 'handleMarkAsLost',
          type: 'info',
        });
      }
    });

    const isOrderAssignedInMaster = useCallback(
      (orderId: number): boolean => {
        return (
          !!masterScenario?.schedule.find(
            (entry) => isCargoEntry(entry) && entry.orderId === orderId,
          ) ||
          !!masterScenario?.coaAssignments.find(
            (entry) => entry.orderId === orderId,
          ) ||
          !!masterScenario?.spotAssignments.find(
            (entry) => entry.orderId === orderId,
          )
        );
      },
      [masterScenario],
    );

    const handleDelete = useEvent(async (orderIds: number[]) => {
      if (orderIds.length === 0) {
        notify('No orders selected for deletion', {
          toastId: 'handleDelete',
          type: 'warning',
        });
        return;
      }
      try {
        for (const orderId of orderIds) {
          if (isOrderAssignedInMaster(orderId)) {
            notify(
              `Unable to delete order${
                orderIds.length > 1 ? 's' : ''
              }: Order ${orderId} is assigned in master scenario and cannot be deleted until you commit the assignment's deletion.`,
              {
                toastId: 'handleDelete',
                type: 'error',
                autoClose: false,
              },
            );
            return;
          }
        }

        notify(`Deleting order${orderIds.length > 1 ? 's' : ''} ...`, {
          toastId: 'handleDelete',
          type: 'info',
        });

        hideRowContextMenu();

        await softDeleteOrders(apolloClient, orderIds);

        notify(`Order${orderIds.length > 1 ? 's' : ''} deleted.`, {
          toastId: 'handleDelete',
          type: 'success',
        });
      } catch (err) {
        notify(
          `Unable to delete order${orderIds.length > 1 ? 's' : ''}: ${
            err.message
          }`,
          {
            toastId: 'handleDelete',
            type: 'error',
          },
        );
      }
    });

    const onDblClick = useCallback(
      (
        _event: React.MouseEvent<Element, MouseEvent>,
        row: Row<OrderTableRowData>,
      ) => {
        const target = _event.target as Element;
        // to exclude context menu button from double click
        const isButton = target.className.includes('Button__ButtonText');

        // if clicked on context menu button, ignore double click
        if (isButton) {
          _event.stopPropagation();
          _event.preventDefault();
          return;
        }
        const incomingVoyageCharterId = getRowIncomingVoyageCharterId(row);

        if (
          planningMode === 'voyage_charter' &&
          incomingVoyageCharterId !== null
        ) {
          onViewVoyageCharter(incomingVoyageCharterId);
        } else {
          onViewOrder(row.original?.id);
        }
      },
      [onViewOrder, onViewVoyageCharter, planningMode],
    );

    const selectedOrderIds = useMemo(
      () => new Set(selectedOrders),
      [selectedOrders],
    );

    const onSuggest = useEvent(() => {
      if (!currentContextRow.current) return;
      const orderId = currentContextRow.current.original.id;
      const orderNotSelected = !selectedOrders.includes(orderId);
      if (orderNotSelected) {
        const orderIds = [orderId];
        onSelectOrder(orderIds);
        setTimeout(() => onSuggestVoyage(), 0);
      } else {
        onSuggestVoyage(selectedOrders);
      }
    });

    const onMoveColumn = useEvent(
      (
        movedColumnId: string,
        targetColumnId: string,
        position: 'before' | 'after',
      ) => {
        setColumnOrder((curOrder) => {
          const newOrder = curOrder.flatMap((columnId) => {
            // Remove column form previous position
            if (columnId === movedColumnId) return [];
            // Insert column at new position
            if (columnId === targetColumnId)
              return position === 'before'
                ? [movedColumnId, columnId]
                : [columnId, movedColumnId];
            return [columnId];
          });

          saveColumnOrder(newOrder);

          return newOrder;
        });
      },
    );

    const onResetColumnOrder = useEvent(() => {
      const defaultOrder = getDefaultColumnOrder(columns);
      setColumnOrder(defaultOrder);
      saveColumnOrder(defaultOrder);
    });

    const isRowLocked = useCallback(
      (row: Row<OrderTableRowData>): boolean => {
        return editedRows.has(row.index) || !!row.original?.tempId;
      },
      [editedRows],
    );
    const isReadOnlyScenario = scenario?.isReadOnly;
    const RenderRow = useCallback(
      ({ size, start, index, key }: VirtualItem<HTMLDivElement>) => {
        const row = rowsToRender.at(index);
        if (!row) return null;
        prepareRow(row);
        const { style: rowStyle, ...restRowProps } = row.getRowProps();

        const style: React.CSSProperties = {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: `${size}px`,
          transform: `translateY(${start}px)`,
          ...rowStyle,
        };

        if (row.isGrouped) {
          const weekNumber =
            row.subRows[0]?.original.startOfWeek ??
            row.original?.startOfWeek ??
            null;

          return (
            <div style={style} key={key}>
              <WeekIndicatorRow
                columnsWidth={totalColumnsWidth}
                startOfWeek={weekNumber}
              />
            </div>
          );
        }

        const isActive = selectedOrderIds.has(row.original?.id);

        return (
          <div {...restRowProps} key={key} style={style}>
            <OrderTableRow
              row={row}
              hiddenColumns={hiddenColumns} // Provided only so the memo can still trigger a re-render when columns changed but data did not
              isLocked={isRowLocked(row)}
              index={index}
              isMuted={index % 2 === 1}
              isActive={isActive}
              onSelect={selectOrdersFromRows}
              selectedOrderIds={selectedOrderIds}
              onContextMenu={onContextMenu}
              onDblClick={onDblClick}
              selectionMode={selectionMode}
              isReadOnlyScenario={isReadOnlyScenario ?? false}
            />
          </div>
        );
      },
      [
        rowsToRender,
        prepareRow,
        selectedOrderIds,
        hiddenColumns,
        isRowLocked,
        selectOrdersFromRows,
        onContextMenu,
        onDblClick,
        selectionMode,
        isReadOnlyScenario,
        totalColumnsWidth,
      ],
    );

    const scrollRef = useRef<HTMLDivElement | null>(null);

    const rowVirtualizer = useVirtualizer({
      count: rowsToRender.length,
      getScrollElement: () => scrollRef.current,
      estimateSize: (index) =>
        rowsToRender.at(index)?.isGrouped ? WEEK_INDICATOR_HEIGHT : 40,
      overscan: 2,
      paddingEnd: 10,
    });

    /** Context menu actions affect all the selected orders when menu opened on a selected order, otherwise only the current row */
    const getContextMenuOrders = useCallback(() => {
      const row = currentContextRow.current;
      if (!row?.original) return [];
      return selectedOrders.includes(row.original.id)
        ? selectedOrders
        : [row.original.id];
    }, [selectedOrders]);

    const isContextMenuDisabled =
      currentContextRow.current && isRowLocked(currentContextRow.current);

    const { scrollToOrderId } = useScrollToOrder({
      rowsToRender,
      rowVirtualizer,
    });
    useImperativeHandle(ref, () => ({
      scrollToOrderId,
    }));

    const contextMenuStore = useContextStore<OrderMenuContextValue>({
      getItemMenuButtonProps,
    });

    return (
      <>
        <OrderMenuContext.Provider value={contextMenuStore}>
          <Box height="100%" width="100%">
            <AutoSizer disableWidth>
              {({ height }) => (
                <div
                  data-testid="order-table-scroller"
                  style={{ width: '100%', height, overflow: 'auto' }}
                  ref={scrollRef}
                >
                  <div {...getTableProps()} aria-label="Orders">
                    <div
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        borderBottomColor: '#00000078',
                        borderBottomWidth: '1px',
                        borderBottomStyle: 'solid',
                      }}
                    >
                      {headerGroups.map((headerGroup) => (
                        // eslint-disable-next-line react/jsx-key
                        <TableHeaders
                          as="div"
                          isSticky
                          {...headerGroup.getHeaderGroupProps()}
                        >
                          {headerGroup.headers.map(
                            (
                              column: HeaderGroup<OrderTableRowData> & {
                                align: 'right' | 'left';
                              },
                            ) => (
                              <OrderTableHeader
                                key={column.id}
                                column={column}
                                onMoveColumn={onMoveColumn}
                                onContextMenu={onHeaderContextMenu}
                              />
                            ),
                          )}
                        </TableHeaders>
                      ))}
                    </div>
                    <div
                      {...getTableBodyProps()}
                      style={{
                        position: 'relative',
                        zIndex: 0,
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: '100%',
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map(RenderRow)}
                      {rowsToRender.length < 1 && (
                        <NotFoundOrder
                          height={height}
                          isSearching={isSearching}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </AutoSizer>
          </Box>

          {isHeaderContextMenuVisible && (
            <div ref={setHeaderContextMenuRef} style={headerContextMenuStyles}>
              <Menu hasShadow>
                <MenuItem
                  label="Reset column order"
                  onPress={onResetColumnOrder}
                />
              </Menu>
            </div>
          )}

          {isRowContextMenuVisible && !isContextMenuDisabled && (
            <div ref={setRowContextMenuRef} style={rowContextMenuStyles}>
              <Menu hasShadow>
                {isSuggestAvailable && (
                  <>
                    <MenuItem label="Suggest voyage" onPress={onSuggest} />
                  </>
                )}

                {voyageCharterMode &&
                currentContextRow.current &&
                getRowIncomingVoyageCharterId(currentContextRow.current) !==
                  null ? (
                  <MenuItem
                    label="Duplicate cargo"
                    onPress={() => {
                      if (!currentContextRow.current) return;
                      const voyageCharterId = getRowIncomingVoyageCharterId(
                        currentContextRow.current,
                      );
                      if (voyageCharterId === null) return;
                      onDuplicateVoyageCharter(voyageCharterId);
                    }}
                  />
                ) : (
                  <MenuItem
                    label="Duplicate order"
                    onPress={() =>
                      currentContextRow.current &&
                      onDuplicateOrder(currentContextRow.current.original.id)
                    }
                  />
                )}

                <MenuItem
                  label="Duplicate and offset by days"
                  onPress={() =>
                    currentContextRow.current &&
                    onDuplicateAndOffsetSelectedOrders(
                      currentContextRow.current.original.id,
                    )
                  }
                />
                <MenuItem
                  label="Batch edit orders"
                  onPress={() =>
                    currentContextRow.current &&
                    onBatchEditSelectedCargoes(
                      currentContextRow.current.original.id,
                    )
                  }
                />
                {voyageCharterMode && (
                  <>
                    <MenuSeparator />
                    <MenuItem
                      label="Edit current row"
                      onPress={() => {
                        const rowIndex = currentContextRow.current?.index;
                        if (rowIndex) {
                          onEditHandler(rowIndex);
                        }
                      }}
                    />
                  </>
                )}
                {voyageCharterMode && inquiryMode && (
                  <MenuItem
                    label="Add new parcel (same cargo)"
                    onPress={() => {
                      const rowIndex = currentContextRow.current?.index;
                      const order = currentContextRow.current?.original;
                      if (rowIndex && order) {
                        onAddOrder(order, true);
                      }
                    }}
                  />
                )}
                {selectedStatusFilter === 'UNPUBLISHED' && (
                  <>
                    <MenuSeparator />
                    <MenuItem
                      label="Publish"
                      onPress={() => void handlePublish(getContextMenuOrders())}
                    />
                  </>
                )}
                <MenuItem
                  label="Archive"
                  onPress={() => void handleArchive(getContextMenuOrders())}
                />
                {voyageCharterMode &&
                  selectedStatusFilter === 'INQUIRY' &&
                  hasFinancialRights && (
                    <MenuItem
                      label="Mark as lost contract"
                      onPress={() =>
                        void handleMarkAsLost(getContextMenuOrders())
                      }
                    />
                  )}
                <MenuItem
                  label="Delete"
                  onPress={() => void handleDelete(getContextMenuOrders())}
                />
              </Menu>
            </div>
          )}
        </OrderMenuContext.Provider>
      </>
    );
  },
);

const orderTableColumnOrderAtom = persistedAtom({
  schema: zod.array(zod.string()),
  key: 'order_table_column_order',
  defaultValue: [],
  getOnInit: true,
});

const draggedHeaderAtom = atom<{
  id: string;
  width: number;
} | null>(null);

function getDragTargetPosition(event: React.DragEvent<HTMLElement>) {
  const targetRect = event.currentTarget.getBoundingClientRect();
  const mouseX = event.clientX;
  const midpointX = targetRect.left + targetRect.width / 2;

  return mouseX > midpointX ? 'after' : 'before';
}

function OrderTableHeader({
  column,
  onContextMenu,
  onMoveColumn,
}: {
  column: HeaderGroup<OrderTableRowData> & {
    align: 'right' | 'left';
  };
  onContextMenu: (event: React.MouseEvent<Element, MouseEvent>) => void;
  onMoveColumn: (
    movedColumnId: string,
    targetColumnId: string,
    position: 'after' | 'before',
  ) => void;
}) {
  const [draggedHeader, setDraggedHeader] = useAtom(draggedHeaderAtom);
  const [dragTargetSide, setDragTargetSide] = useState<
    'after' | 'before' | null
  >(null);

  const { style, ...restHeaderProps } = column.getHeaderProps(
    column.getSortByToggleProps(),
  );

  function onDragStart(event: React.DragEvent<HTMLDivElement>) {
    const width = event.currentTarget.offsetWidth;

    setDraggedHeader({
      id: column.id,
      width,
    });

    event.dataTransfer.dropEffect = 'move';
  }

  function onDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault();

    // Prevent dragover event if the target is the same as the element being dragged
    if (draggedHeader?.id === column.id) {
      return;
    }

    setDragTargetSide(getDragTargetPosition(event));
  }

  function onDragLeave() {
    setDragTargetSide(null);
  }

  function onDrop(event: React.DragEvent<HTMLDivElement>) {
    if (draggedHeader) {
      const side = getDragTargetPosition(event);

      onMoveColumn(draggedHeader.id, column.id, side);
    }

    setDraggedHeader(null);
    setDragTargetSide(null);
  }

  const isWeekHeader = column.id === 'week';
  const isEditHeader = column.id === 'edit';

  if (isWeekHeader) {
    return null;
  }

  return (
    <>
      <TableHeader
        as="div"
        style={{
          ...style,
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start',
          alignItems: 'flex-start',
          left: isWeekHeader ? '-40px' : '0px',
          marginRight:
            dragTargetSide === 'after' ? (draggedHeader?.width ?? 0) : 0,
          marginLeft:
            dragTargetSide === 'before' ? (draggedHeader?.width ?? 0) : 0,
          position: 'relative',
        }}
        draggable
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onContextMenu(e);
        }}
        {...restHeaderProps}
      >
        {isEditHeader ? '' : column.render('Header')}
        {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
        {dragTargetSide !== null && draggedHeader !== null && (
          <DropTargetIndicator
            width={draggedHeader.width}
            position={dragTargetSide}
          />
        )}
      </TableHeader>
    </>
  );
}

function DropTargetIndicator(props: {
  width: number;
  position: 'after' | 'before';
}) {
  return (
    <Box
      width={props.width}
      height="100%"
      flexShrink={0}
      flexGrow={0}
      bg="neutral.gray_225"
      position="absolute"
      top={0}
      right={props.position === 'after' ? -props.width : undefined}
      left={props.position === 'before' ? -props.width : undefined}
      p={2}
    >
      <Box
        height="100%"
        borderWidth={1}
        borderColor="neutral.gray_250"
        borderStyle="dashed"
      />
    </Box>
  );
}

export default React.memo(OrderTable);
